import { Transition } from "@headlessui/react";
import clsxml from "clsx";
import isString from "lodash/isString";
import { useCallback, useEffect, useRef, useState } from "react";

import Dropdown, { DropdownOption } from "../Dropdown/Dropdown";
import { Icon24Cancel, Icon24SelectPicker } from "../Icons";
import Input from "../Input/Input";
import { useOutsideEvent } from "../../hooks";
import { useAutoScrollToFieldWithError } from "../../hooks/useAutoScrollToFieldWithError";

export interface SelectProps {
  name: string;
  value?: string | string[];
  filterable?: boolean;
  label?: React.ReactNode;
  multi?: boolean;
  disabled?: boolean;
  freeze?: boolean;
  error?: string;
  options: DropdownOption[];
  className?: string;
  onChange: (value: string | string[]) => void;
  onSearchChange?: (value: string) => void;
  onDisabledToggle?: (value: boolean) => void;
}

const Select = (props: SelectProps) => {
  const { onSearchChange } = props;
  const [isOpen, setIsOpen] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<
    DropdownOption[] | null
  >(null);
  const [filteredOptions, setFilteredOptions] = useState(props.options);
  const [filter, setFilter] = useState<string>("");
  const selectElement = useRef<HTMLDivElement | null>(null);

  useAutoScrollToFieldWithError(
    props.name,
    selectElement.current,
    !!props.error
  );

  useOutsideEvent(selectElement, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    if (props.value) {
      setSelectedOptions(
        props.options.filter((item) =>
          Array.isArray(props.value)
            ? props.value.includes(item.value)
            : item.value === props.value
        )
      );
    } else {
      setSelectedOptions(null);
    }
  }, [setSelectedOptions, props]);

  useEffect(() => {
    setFilteredOptions(props.options);
  }, [props.options]);

  const handleSetFilter = useCallback(
    (value: string) => {
      setFilter(value);
      onSearchChange?.(value);
    },
    [onSearchChange, setFilter]
  );

  const handleInputChange = useCallback(
    (value: string) => {
      const filterRegexp = new RegExp(value + ".*", "i");
      const filteredOptions = props.options.filter(
        (option) => isString(option.title) && filterRegexp.test(option.title)
      );

      setFilteredOptions(filteredOptions);
      handleSetFilter(value);
      setSelectedOptions(null);
    },
    [setSelectedOptions, setFilteredOptions, props.options, handleSetFilter]
  );

  const handleRemoveValue = useCallback(() => {
    setFilteredOptions(props.options);
    setSelectedOptions(null);
    setIsOpen(false);
    handleSetFilter("");
    Array.isArray(props.value) ? props.onChange([]) : props.onChange("");
  }, [setSelectedOptions, setFilteredOptions, props, handleSetFilter]);

  function getInputValue(options: DropdownOption[] | null): string {
    if (!options) {
      return filter;
    }

    return options?.map((item) => item.title).join("; ");
  }

  const handleItemSelect = useCallback(
    (option: DropdownOption) => {
      setFilteredOptions(props.options);

      let options = selectedOptions ?? [];

      if (props.multi) {
        if (option.checked) {
          options = options.filter((item) => item.value !== option.value);
        } else {
          options.push(option);
        }
        props.onChange(options.map((item) => item.value));
      } else {
        options = [option];
        props.onChange(option.value);
        setIsOpen(false);
      }

      setSelectedOptions([...options]);
      handleSetFilter("");
    },
    [
      setSelectedOptions,
      selectedOptions,
      setIsOpen,
      props,
      setFilteredOptions,
      handleSetFilter,
    ]
  );

  const handleInputBlur = useCallback(() => {
    // @todo сделано чтобы handleSelect успевал отработать первым (иначе не работает выбор из отфильтрованного списка)
    setTimeout(() => {
      setFilteredOptions(props.options);
      handleSetFilter("");
    }, 500);
  }, [handleSetFilter, setFilteredOptions, props.options]);

  return (
    <div ref={selectElement} className={clsxml("relative", props.className)}>
      <Input
        name={props.name}
        label={props.label}
        error={props.error}
        active={isOpen}
        disabled={props.disabled}
        freeze={props.freeze || !props.filterable}
        onDisabledToggle={props.onDisabledToggle}
        onFocus={() => setIsOpen(true)}
        onBlur={handleInputBlur}
        onClick={() => !props.filterable && setIsOpen(true)}
        value={getInputValue(selectedOptions)}
        onChange={handleInputChange}
        after={() => (
          <>
            {!!selectedOptions?.length && (
              <div className="mr-2">
                <Icon24Cancel
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleRemoveValue();
                  }}
                />
              </div>
            )}
            <Icon24SelectPicker
              active={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            />
          </>
        )}
      />
      <Transition
        show={isOpen && !props.disabled}
        enter="transition-opacity duration-200"
        enterFrom="opacity-100"
        enterTo="opacity-100"
        leave="transition-opacity duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-100 hidden"
      >
        <Dropdown
          className="mt-1 max-h-72 overflow-y-scroll"
          options={
            filteredOptions.length
              ? filteredOptions.map((item) => ({
                  ...item,
                  checked: !!selectedOptions?.find(
                    (option) => item.value === option.value
                  ),
                }))
              : [
                  {
                    title: "Nothing found",
                    value: "",
                    disabled: true,
                  },
                ]
          }
          onSelect={handleItemSelect}
        />
      </Transition>
    </div>
  );
};

export default Select;
